/**
 * 1:1 · 이 멤버가 아는 것 (시안 04 D2e). 1:1 상단 수첩 아이콘에서 엽니다.
 * 그 멤버만 아는 항목만 보이고, 별채대화에서 들은 것은 개수 + 「수첩 전체 ›」(→ D2c)로만.
 */
import { useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getMember } from '@config/members/index.js';
import { withJosa } from '@shared/korean.js';
import * as meApi from '../../api/me.js';
import { useMemoBook } from '../../stores/memo.js';
import { Avatar } from '../Avatar.jsx';
import { BottomSheet } from '../BottomSheet.jsx';
import { MemoEditSheet, MemoItem } from './Memo.jsx';
import { track } from '../../api/track.js';
import { useNoteSheet } from '../../stores/noteSheet.js';

export function MemberMemoSheet({ memberKey, open, onClose }) {
    const navigate = useNavigate();
    const [data, setData] = useState({ own: [], shared: 0, loaded: false });
    const [editing, setEditing] = useState(null);
    const hiddenIds = useMemoBook((state) => state.hiddenIds);
    const openNoteSheet = useNoteSheet((state) => state.open);
    const lastSaved = useNoteSheet((state) => state.lastSaved);
    // 적기 시트가 열려 있으면 이 시트는 잠시 내립니다(시트 두 장이 겹치지 않게)
    const noteOpen = useNoteSheet((state) => Boolean(state.request));
    const name = getMember(memberKey)?.name ?? '';

    useEffect(() => {
        if (!open) {
            return undefined;
        }
        // 수첩 연 비율 (명세 v1.3 10장, D44)
        track('memo_open', { from: 'chat' });
        const controller = new AbortController();
        Promise.all([
            meApi.fetchMemories({ signal: controller.signal, scope: 'chat', member: memberKey }),
            meApi.fetchMemories({ signal: controller.signal, scope: 'room' }),
        ])
            .then(([own, shared]) => setData({ own: own.memories, shared: shared.memories.length, loaded: true }))
            .catch(() => setData((previous) => ({ ...previous, loaded: true })));
        return () => {
            controller.abort();
        };
        // 적기 시트로 적으면 다시 읽습니다
    }, [open, memberKey, lastSaved?.at]);

    const own = data.own.filter((memory) => !hiddenIds.includes(memory.memoriesId));

    return (
        <>
            <BottomSheet open={open && !editing && !noteOpen} onClose={onClose} label={`${name}이 아는 것`}>
                <div className="flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                        <Avatar memberKey={memberKey} size={40} />
                        <div className="flex flex-col">
                            <h2 className="m-0 text-[17px] leading-6 font-bold">
                                {withJosa(name, '이', '가')} 아는 것
                            </h2>
                            <span className="text-[12px] leading-4 text-text-secondary">
                                1:1에서 한 얘기 · 다른 멤버는 몰라요
                            </span>
                        </div>
                    </div>
                    {data.loaded && !own.length ? (
                        <p className="m-0 py-2 text-[14px] text-text-secondary">
                            {withJosa(name, '과', '와')} 둘이 한 얘기는 아직 없어요.
                        </p>
                    ) : null}
                    {own.length ? (
                        <ul className="m-0 list-none p-0">
                            {own.map((memory) => (
                                <MemoItem key={memory.memoriesId} memory={memory} onEdit={setEditing} />
                            ))}
                        </ul>
                    ) : null}
                    <button
                        type="button"
                        onClick={() =>
                            openNoteSheet({
                                entry: 'one_on_one',
                                fixedMember: memberKey,
                                key: `add-${memberKey}-${Date.now()}`,
                            })
                        }
                        className="flex h-11 w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-brand/45 bg-surface text-[15px] font-semibold text-brand"
                    >
                        <Plus size={18} strokeWidth={1.8} aria-hidden="true" />
                        {name}에게 알려 두기
                    </button>
                    <div className="flex items-center justify-between border-t border-hairline pt-3">
                        <span className="text-[13px] text-text-secondary">
                            별채대화에서 들은 것도 알아요 · {data.shared}
                        </span>
                        <button
                            type="button"
                            onClick={() => {
                                onClose();
                                navigate('/me/memo');
                            }}
                            className="min-h-touch px-1 text-[13px] font-semibold text-brand"
                        >
                            수첩 전체 ›
                        </button>
                    </div>
                </div>
            </BottomSheet>
            <MemoEditSheet memory={editing} onClose={() => setEditing(null)} />
        </>
    );
}
